import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSubscription } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { renderTracking } from '@r-services/render-tracking';
import { SkuAnnotationToolbar, useSkuAnnotation } from '../sku-annotations';

import { BlueprintAddPageGhost } from './shared/blueprint-add-page-ghost';
import { BlueprintBacksplashPage } from './pages/blueprint-backsplash-page';
import { BlueprintCombinedFrontBackPage } from './pages/blueprint-combined-front-back-page';
import { BlueprintCoverPage } from './pages/blueprint-cover-page';
import { BlueprintCustomPagesProvider, useBlueprintCustomPages } from './shared/blueprint-custom-pages-context';
import { BlueprintCustomPanelPage } from './pages/blueprint-custom-panel-page';
import { BlueprintLandingZonePage } from './pages/blueprint-landing-zone-page';
import { AnnotationPresetsPanel } from './shared/annotation-presets-panel';
import { BLUEPRINT_PAGE_SVG_H, BLUEPRINT_PAGE_SVG_W } from './shared/blueprint-page-annotation-overlay';
import { BlueprintExportOverlay } from './shared/blueprint-export-overlay';
import { BlueprintFlatTrimPage } from './pages/blueprint-flat-trim-page';
import { BlueprintJointCountertopPage } from './pages/blueprint-joint-countertop-page';
import { BlueprintKitchenOverviewPage } from './pages/blueprint-kitchen-overview-page';
import { BlueprintLoadingSpinner } from './shared/blueprint-loading-spinner';
import { BlueprintOverridesProvider } from './shared/blueprint-overrides-context';
import { BlueprintPageAnnotationOverlay } from './shared/blueprint-page-annotation-overlay';
import { BlueprintPageFontControl } from './shared/blueprint-page-font-control';
import { BlueprintOverheadLayoutPage } from './pages/blueprint-overhead-layout-page';
import { BlueprintPageFooter } from './shared/blueprint-page-footer';
import { BlueprintPageHeader } from './shared/blueprint-page-header';
import { BlueprintPagesContext } from './shared/blueprint-pages-context';
import { OverviewFitProvider } from './shared/overview-fit-context';
import { BlueprintPageWrapper } from './shared/blueprint-page-wrapper';
import { BlueprintPanelPage } from './pages/blueprint-panel-page';
import { BlueprintRevisionLogPage } from './pages/blueprint-revision-log-page';
import { exportBlueprintToPdf, exportBlueprintToPptx } from './helpers/export-blueprint';
import { fetchAsDataUrl } from '../../capture';
import { ADD_PAGE_GHOST_HEIGHT_PX, ADD_PAGE_GHOST_MARGIN_PX, EXPORT_ERROR_STYLE, exportButtonStyle, NAV_BAR_STYLE, NAV_BUTTON_STYLE, SCROLL_CONTAINER_STYLE } from './shared/blueprint-view-styles';
import { getBlueprintCreationDate } from './helpers/get-blueprint-creation-date';
import { getBlueprintPages } from './helpers/get-blueprint-pages';
import { getLayoutHeaderDescription } from './helpers/get-layout-header-description';
import { getMaxRevisionNumber } from './helpers/get-max-revision-number';
import { raceFrame } from './helpers/race-frame';
import { stampInitialBlueprintDate, stampRevisionRows } from './helpers/stamp-blueprint-dates';
import { LazyBlueprintPage } from './shared/lazy-blueprint-page';
import type { BlueprintPage } from './types';

export type BlueprintViewContainerProps = {
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
	designerInitials?: string;
	currentUserInitials?: string;
	/** Fired after a successful download — react-ui saves the design, so the dates the export just stamped are persisted. */
	onDownloadPdf?: () => void;
	onDownloadPptx?: () => void;
};

const BlueprintViewContainerInner = (props: BlueprintViewContainerProps = {}): React.ReactElement => {
	const {
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl,
		designerInitials,
		currentUserInitials,
		onDownloadPdf,
		onDownloadPptx
	} = props;

	const context = useContext(AppContext);
	const { controller } = context;
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const pageIndexRef = useRef(0);
	const pageCounterRef = useRef<HTMLSpanElement>(null);
	const [isExporting, setIsExporting] = useState(false);
	const [forceAllVisible, setForceAllVisible] = useState(false);
	const [exportImageUrl, setExportImageUrl] = useState<string | null>(null);
	const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
	const [exportError, setExportError] = useState<string | null>(null);
	const { activeTool, setActiveTool, selectedAnnotationId, setSelectedAnnotationId, editingAnnotationId, setEditingAnnotationId } = useSkuAnnotation();
	const { customPages, addCustomPage } = useBlueprintCustomPages();

	useSubscription(controller ?? null, false, [['blueprint-view-three-d-image-url', 'updated']]);

	// Live-refresh pages on kitchen mutation; 'kitchen/updated' is excluded so EditableText keystrokes don't re-render the contentEditable mid-edit and drop the caret.
	// island_size/island_axis/island_position and the connection setters are included so a live resize,
	// rotate, or re-alignment busts `pages`' useMemo below -- without them the memo's `[kitchen]` dep never
	// changes (the Kitchen instance mutates in place), so a resize left the Kitchen Overview / per-island
	// page letters and dimensions showing the design's shape from BEFORE the edit until a remount (GitHub
	// "islands out of order" reports: the letters matched a stale layout, not the one on screen).
	const [kitchenRefreshEvent] = useSubscription(controller?.kitchen ?? null, false, [
		['kitchen_fuel', 'updated'],
		['surround_material', 'updated'],
		['countertop_material', 'updated|reset'],
		['sku_annotation', 'updated'],
		// Trim profile on the Kitchen Overview sheet is derived from the trims, so a per-corner convert has to repaint it.
		['panel_trim', 'updated'],
		['panel_trims', 'reset'],
		// Live-refresh when a breakpoint is dragged (segment measurements recompute from bp.center) or
		// a measurement is hidden/unhidden in the BP view. Both mutate the shared model but the sheet
		// never repainted without these, so the measurements didn't follow the drag and the hide
		// toggle looked dead (GitHub #293).
		['breakpoint_center', 'updated'],
		['dimension', 'updated'],
		['island|panel|panel_item|panel_item_group|item|countertop|obstruction|connection', 'created|loaded|deleted'],
		['island_size|island_axis|island_position|connection_padding|connection_offset|connection_vertical_align|connection_source_panel', 'updated']
	]);

	// Click-outside deselection: the per-page overlay <svg> is pointer-events:none in select mode (so page text stays editable), so it can't catch background clicks itself. A document listener clears selection unless the click landed on an annotation or the toolbar.
	useEffect(() => {
		if (!selectedAnnotationId) return;
		const handleOutsideMouseDown = (e: MouseEvent): void => {
			const target = e.target as Element | null;
			if (!target) return;
			if (target.closest('[data-bp-annotation]') || target.closest('[data-sku-toolbar]')) return;
			if (editingAnnotationId) (document.activeElement as HTMLElement | null)?.blur();
			setSelectedAnnotationId(null);
			setEditingAnnotationId(null);
		};
		const timer = setTimeout(() => document.addEventListener('mousedown', handleOutsideMouseDown), 0);
		return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleOutsideMouseDown); };
	}, [selectedAnnotationId, editingAnnotationId, setSelectedAnnotationId, setEditingAnnotationId]);

	if (!controller) return <BlueprintLoadingSpinner />;

	renderTracking.incrementRender('BlueprintViewContainer');

	const { kitchen, blueprint } = controller;
	const liveThreeDImageUrl = blueprint?.thumbnail || blueprint?.metadata?.canonicalRenderingPath || controller.options.threeDImageUrl || threeDImageUrl;

	// kitchenRefreshEvent isn't read here -- it's a dependency purely to bust this memo when the
	// subscription above fires, since `kitchen` is the same mutated-in-place instance across those events.
	const pages = useMemo(
		() => getBlueprintPages({ kitchen, customPages }),
		[kitchen, kitchenRefreshEvent, customPages]
	);

	const handleScroll = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const { scrollTop, scrollHeight, clientHeight } = container;
		const totalPages = pages.length;
		// The add-page ghost trails the last sheet, so the scrollable range the real pages occupy
		// ends short of the bottom — counting against the full range reports pages ahead of the one on screen.
		const pagesScrollRange = scrollHeight - clientHeight - (ADD_PAGE_GHOST_HEIGHT_PX + ADD_PAGE_GHOST_MARGIN_PX);
		const scrollPerPage = Math.max(pagesScrollRange, 1) / Math.max(totalPages - 1, 1);
		const newPage = Math.min(Math.round(scrollTop / scrollPerPage), totalPages - 1);

		if (newPage !== pageIndexRef.current) {
			pageIndexRef.current = newPage;
			if (pageCounterRef.current) {
				pageCounterRef.current.textContent = `Page ${newPage + 1} of ${totalPages}`;
			}
		}
	}, [pages.length]);

	const scrollToPage = useCallback((index: number) => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const pageElements = container.querySelectorAll('[data-blueprint-page]');
		if (pageElements[index]) {
			pageElements[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, []);

	// #354: which page a placed preset lands on -- read at click time, not reactive state, since pageIndexRef only updates on scroll
	const getCurrentPageNumber = useCallback((): number => pages[pageIndexRef.current]?.pageNumber ?? 1, [pages]);

	// #354 audit: "viewport center" is the visible region of the current page, not its static midpoint -- there's no horizontal scroll/zoom in this view, so only the vertical scroll intersection matters
	const getViewportCenter = useCallback((): { x: number; y: number } => {
		const fallback = { x: BLUEPRINT_PAGE_SVG_W / 2, y: BLUEPRINT_PAGE_SVG_H / 2 };
		const container = scrollContainerRef.current;
		if (!container) return fallback;
		const pageElements = container.querySelectorAll<HTMLElement>('[data-blueprint-page]');
		const pageEl = pageElements[pageIndexRef.current];
		if (!pageEl) return fallback;
		const pageRect = pageEl.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();
		if (pageRect.height <= 0) return fallback;
		const visibleTop = Math.max(pageRect.top, containerRect.top);
		const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
		if (visibleBottom <= visibleTop) return fallback;
		const visibleCenterYClient = (visibleTop + visibleBottom) / 2;
		const svgY = ((visibleCenterYClient - pageRect.top) / pageRect.height) * BLUEPRINT_PAGE_SVG_H;
		return { x: BLUEPRINT_PAGE_SVG_W / 2, y: Math.min(Math.max(svgY, 0), BLUEPRINT_PAGE_SVG_H) };
	}, []);

	const handleProgress = useCallback((current: number, total: number): void => {
		setExportProgress({ current, total });
	}, []);

	// Resolves true only when a document was actually produced, so the caller's post-export save never
	// runs on a failed capture.
	const runExport = useCallback(async (exportFn: typeof exportBlueprintToPdf): Promise<boolean> => {
		const container = scrollContainerRef.current;
		if (!container || isExporting) return false;

		// The export is what dates the sheet: the BP's creation date freezes on the first one, and every
		// export freezes the date on each revision row filled in since. Both persist on the next save.
		stampInitialBlueprintDate({ kitchen, blueprint });
		stampRevisionRows({ kitchen, currentUserInitials, commitDates: true });

		// Resolved at click time so manually edited Rev cells are reflected in the filename.
		const maxRevision = getMaxRevisionNumber({ kitchen });
		// Customer name leads, BP_ follows — the convention the team hand-renames every download to.
		const exportFileName = `${customerName ?? 'Customer'}_BP_${layoutDescription ?? 'Design'}${maxRevision !== null ? `_Rev${maxRevision}` : ''}`;

		setIsExporting(true);
		setForceAllVisible(true);
		setExportProgress({ current: 0, total: 0 });
		setExportError(null);

		// Inline the 3D rendering as a data URL: the Azure blob lacks CORS headers, so the
		// crossOrigin <img> load html2canvas uses to rasterize is blocked and the image drops out.
		if (liveThreeDImageUrl && /^https?:/.test(liveThreeDImageUrl)) {
			setExportImageUrl(await fetchAsDataUrl(liveThreeDImageUrl, 'reload'));
		}

		// Ensure swapped-in render imgs are decoded before placements are measured and html2canvas runs.
		const renderImgs = Array.from(container.querySelectorAll<HTMLImageElement>('[data-blueprint-render]'));
		await Promise.allSettled(renderImgs.map((img) => img.decode()));

		// let force-mounted panel cards finish their getBBox stabilization before capture (~40 frames)
		await new Promise<void>((resolve) => {
			let ticks = 0;
			const step = (): void => {
				ticks += 1;
				if (ticks >= 40) {
					resolve();
					return;
				}
				raceFrame(step);
			};
			raceFrame(step);
		});

		try {
			await exportFn({ container, fileName: exportFileName, onProgress: handleProgress });
			return true;
		} catch (error) {
			// Uncaught, this rejection was invisible — the export just appeared to do nothing.
			console.error('[BlueprintView] export failed', error);
			setExportError(error instanceof Error ? error.message : String(error));
			return false;
		} finally {
			setIsExporting(false);
			setForceAllVisible(false);
			setExportImageUrl(null);
		}
	}, [customerName, layoutDescription, kitchen, blueprint, currentUserInitials, isExporting, handleProgress, liveThreeDImageUrl]);

	const handleDownloadPdf = useCallback(async (): Promise<void> => {
		if (await runExport(exportBlueprintToPdf)) onDownloadPdf?.();
	}, [runExport, onDownloadPdf]);

	const handleDownloadPptx = useCallback(async (): Promise<void> => {
		if (await runExport(exportBlueprintToPptx)) onDownloadPptx?.();
	}, [runExport, onDownloadPptx]);

	const computedLayoutDescription = kitchen ? getLayoutHeaderDescription({ kitchen }) : '';
	const effectiveLayoutDescription = computedLayoutDescription || layoutDescription;

	// The revision log's first row reads this same value back off commonPageProps, so the two can't disagree (#416).
	const effectiveCreationDate = getBlueprintCreationDate({ kitchen, blueprint }) || creationDate;

	const commonPageProps = {
		customerName,
		layoutDescription: effectiveLayoutDescription,
		designerName,
		creationDate: effectiveCreationDate,
		threeDImageUrl: exportImageUrl ?? liveThreeDImageUrl
	};

	const renderPage = (page: BlueprintPage): React.ReactElement => {
		const pageContent = (() => {
			switch (page.pageType) {
				case 'cover':
					return <BlueprintCoverPage page={page} kitchen={kitchen} {...commonPageProps} />;
				case 'kitchen-overview':
					return <BlueprintKitchenOverviewPage page={page} kitchen={kitchen} {...commonPageProps} />;
				case 'revision-log':
					return <BlueprintRevisionLogPage page={page} kitchen={kitchen} designerInitials={designerInitials} currentUserInitials={currentUserInitials} {...commonPageProps} />;
				case 'overhead-layout':
				case 'base-layout':
				case 'countertop-layout':
					return <BlueprintOverheadLayoutPage page={page} blueprint={blueprint} {...commonPageProps} />;
				case 'front-panel':
				case 'side-panels':
				case 'internal-supports':
				case 'back-panel':
				case 'countertop':
					return <BlueprintPanelPage page={page} blueprint={blueprint} {...commonPageProps} />;
				case 'combined-front-back':
					return <BlueprintCombinedFrontBackPage page={page} blueprint={blueprint} {...commonPageProps} />;
				case 'joint-countertop':
					return <BlueprintJointCountertopPage page={page} blueprint={blueprint} {...commonPageProps} />;
				case 'backsplash':
					return <BlueprintBacksplashPage page={page} blueprint={blueprint} kitchen={kitchen} {...commonPageProps} />;
				case 'flat-trim':
					return <BlueprintFlatTrimPage page={page} blueprint={blueprint} kitchen={kitchen} {...commonPageProps} />;
				case 'custom-panel':
					return <BlueprintCustomPanelPage page={page} blueprint={blueprint} {...commonPageProps} />;
				case 'landing-zone':
					return kitchen ? <BlueprintLandingZonePage page={page} blueprint={blueprint} kitchen={kitchen} {...commonPageProps} /> : <></>;
				default:
					return (
						<BlueprintPageWrapper pageNumber={page.pageNumber}>
							<BlueprintPageHeader title={page.title} pageNumber={page.pageNumber} {...commonPageProps} showThumbnail={page.pageNumber > 1} />
							<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#999' }}>
								{page.title}
							</div>
							<BlueprintPageFooter pageNumber={page.pageNumber} />
						</BlueprintPageWrapper>
					);
			}
		})();

		return (
			<div key={`page-${page.pageNumber}`}>
				<LazyBlueprintPage pageNumber={page.pageNumber} forceVisible={forceAllVisible}>
					<BlueprintPageFontControl pageNumber={page.pageNumber} scope={page.overrideScope ?? `page.${page.pageNumber}`}>
						<BlueprintPageAnnotationOverlay pageNumber={page.pageNumber}>
							{pageContent}
						</BlueprintPageAnnotationOverlay>
					</BlueprintPageFontControl>
				</LazyBlueprintPage>
			</div>
		);
	};

	const activeAnnotationId = editingAnnotationId ?? selectedAnnotationId;
	const pageAnnotation = activeAnnotationId ? kitchen?.blueprintAnnotations?.findById(activeAnnotationId) ?? null : null;
	// blueprint COUNTERTOP sheets render kitchen.skuAnnotations, so without this fallback the toolbar's property controls go blank there
	const sheetAnnotation = pageAnnotation || !activeAnnotationId ? null : kitchen?.skuAnnotations?.findById(activeAnnotationId) ?? null;
	const selectedAnnotation = pageAnnotation ?? sheetAnnotation;
	const annotationCollection = (sheetAnnotation ? kitchen?.skuAnnotations : kitchen?.blueprintAnnotations) ?? null;

	return (
		<BlueprintOverridesProvider kitchen={kitchen}>
		<BlueprintPagesContext.Provider value={pages}>
		<OverviewFitProvider>
		<div style={{ width: '100%', height: '100%', position: 'relative' }}>
			<SkuAnnotationToolbar
				activeTool={activeTool}
				onToolChange={(tool) => setActiveTool(tool)}
				selectedAnnotation={selectedAnnotation}
				annotations={annotationCollection}
			/>

			<AnnotationPresetsPanel getCurrentPageNumber={getCurrentPageNumber} getViewportCenter={getViewportCenter} />

			<div style={NAV_BAR_STYLE}>
				<button onClick={() => scrollToPage(Math.max(0, pageIndexRef.current - 1))} style={NAV_BUTTON_STYLE}>{'<'}</button>
				<span ref={pageCounterRef}>Page 1 of {pages.length}</span>
				<button onClick={() => scrollToPage(Math.min(pages.length - 1, pageIndexRef.current + 1))} style={NAV_BUTTON_STYLE}>{'>'}</button>
				<button onClick={handleDownloadPdf} disabled={isExporting} style={{ ...exportButtonStyle(isExporting), marginLeft: '8px' }}>
					{isExporting ? 'Exporting...' : 'PDF'}
				</button>
				<button onClick={handleDownloadPptx} disabled={isExporting} style={exportButtonStyle(isExporting)}>
					{isExporting ? 'Exporting...' : 'PPTX'}
				</button>
				{exportError && (
					<span style={EXPORT_ERROR_STYLE} onClick={() => setExportError(null)} title={`${exportError} (click to dismiss)`}>
						Export failed: {exportError}
					</span>
				)}
			</div>

			<div ref={scrollContainerRef} onScroll={handleScroll} style={SCROLL_CONTAINER_STYLE}>
				{pages.map((page) => renderPage(page))}
				{!isExporting && <BlueprintAddPageGhost onAdd={addCustomPage} />}
			</div>

			{isExporting && <BlueprintExportOverlay current={exportProgress.current} total={exportProgress.total} />}
		</div>
		</OverviewFitProvider>
		</BlueprintPagesContext.Provider>
		</BlueprintOverridesProvider>
	);
};

const BlueprintViewContainerWithCustomPages = (props: BlueprintViewContainerProps = {}): React.ReactElement => {
	const { controller } = useContext(AppContext);

	return (
		<BlueprintCustomPagesProvider kitchen={controller?.kitchen ?? null}>
			<BlueprintViewContainerInner {...props} />
		</BlueprintCustomPagesProvider>
	);
};

export const BlueprintViewContainer = React.memo(BlueprintViewContainerWithCustomPages);
